import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/common/Modal';
import Pagination from '../components/common/Pagination';
import { useDebounce } from '../hooks/useDebounce';
import { SkeletonCard } from '../components/common/Skeleton';

const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const STATUSES = ['todo', 'in_progress', 'done', 'cancelled'];

const priorityMeta = {
  low:    { label: 'Low',    bg: '#f1f5f9', text: '#64748b', border: '#cbd5e1' },
  medium: { label: 'Medium', bg: '#dbeafe', text: '#1d4ed8', border: '#93c5fd' },
  high:   { label: 'High',   bg: '#fef3c7', text: '#92400e', border: '#fcd34d' },
  urgent: { label: 'Urgent', bg: '#fee2e2', text: '#dc2626', border: '#fca5a5' },
};
const statusMeta = {
  todo:        { label: 'To Do',       bg: '#f1f5f9', text: '#475569' },
  in_progress: { label: 'In Progress', bg: '#dbeafe', text: '#1d4ed8' },
  done:        { label: 'Done',        bg: '#d1fae5', text: '#065f46' },
  cancelled:   { label: 'Cancelled',   bg: '#fce7f3', text: '#9d174d' },
};

const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const isOverdue = d => d && new Date(d) < new Date() ;

const defaultForm = {
  title: '', description: '', assigned_to: '', due_date: '', priority: 'medium', status: 'todo',
  entity_type: '', entity_id: '',
};

export default function Tasks() {
  const [data, setData]       = useState({ data: [], total: 0, page: 1, limit: 15 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [statusFilter, setStatusFilter]   = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [page, setPage]       = useState(1);
  const [modal, setModal]     = useState({ open: false, mode: 'add', item: null });
  const [form, setForm]       = useState(defaultForm);
  const [users, setUsers]     = useState([]);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [stats, setStats]     = useState([]);

  const debouncedSearch = useDebounce(search, 300);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, limit: 15,
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
      };
      const { data: res } = await api.get('/tasks', { params });
      setData(res);
    } finally { setLoading(false); }
  }, [page, debouncedSearch, statusFilter, priorityFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/users', { params: { is_active: true } }).then(r => setUsers(r.data.data || [])).catch(() => {});
    api.get('/tasks/stats').then(r => setStats(r.data.data || [])).catch(() => {});
  }, []);

  const openAdd = () => {
    setForm(defaultForm);
    setError('');
    setModal({ open: true, mode: 'add', item: null });
  };

  const openEdit = item => {
    setForm({
      title: item.title || '',
      description: item.description || '',
      assigned_to: item.assigned_to || '',
      due_date: item.due_date ? item.due_date.split('T')[0] : '',
      priority: item.priority || 'medium',
      status: item.status || 'todo',
      entity_type: item.entity_type || '',
      entity_id: item.entity_id || '',
    });
    setError('');
    setModal({ open: true, mode: 'edit', item });
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required.'); return; }
    setSaving(true); setError('');
    try {
      if (modal.mode === 'add') {
        await api.post('/tasks', form);
      } else {
        await api.put(`/tasks/${modal.item.id}`, form);
      }
      setModal({ open: false, mode: 'add', item: null });
      load();
      api.get('/tasks/stats').then(r => setStats(r.data.data || [])).catch(() => {});
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save task.');
    } finally { setSaving(false); }
  };

  const handleStatusToggle = async (task, newStatus) => {
    try {
      await api.patch(`/tasks/${task.id}`, { status: newStatus });
      load();
    } catch {}
  };

  const handleDelete = async id => {
    try {
      await api.delete(`/tasks/${id}`);
      setDeleteConfirm(null);
      load();
    } catch {}
  };

  const statMap = {};
  stats.forEach(s => { statMap[s.status] = Number(s.count); });
  const totalOpen = (statMap.todo || 0) + (statMap.in_progress || 0);

  return (
    <div style={{ padding: '24px', maxWidth: 1100, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0f172a', margin: 0 }}>Tasks</h1>
          <p style={{ color: '#64748b', margin: '4px 0 0', fontSize: '0.875rem' }}>
            {totalOpen} open · {statMap.done || 0} done
          </p>
        </div>
        <button onClick={openAdd} style={{
          padding: '8px 18px', background: '#2563eb', color: '#fff',
          border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: '0.875rem',
        }}>+ New Task</button>
      </div>

      {/* Stats strip */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {STATUSES.map(s => (
          <div key={s} onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
            style={{
              padding: '8px 16px', borderRadius: 8, cursor: 'pointer',
              background: statusFilter === s ? statusMeta[s].text : statusMeta[s].bg,
              color: statusFilter === s ? '#fff' : statusMeta[s].text,
              fontWeight: 600, fontSize: '0.8rem', border: `1px solid ${statusFilter === s ? statusMeta[s].text : '#e2e8f0'}`,
              transition: 'all 0.15s',
            }}>
            {statusMeta[s].label} <span style={{ opacity: 0.75 }}>({statMap[s] || 0})</span>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap' }}>
        <input
          placeholder="Search tasks..."
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(1); }}
          style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: '0.875rem', minWidth: 200 }}
        />
        <select value={priorityFilter} onChange={e => { setPriorityFilter(e.target.value); setPage(1); }}
          style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: '0.875rem' }}>
          <option value="">All Priorities</option>
          {PRIORITIES.map(p => <option key={p} value={p}>{priorityMeta[p].label}</option>)}
        </select>
        {(statusFilter || priorityFilter || search) && (
          <button onClick={() => { setStatusFilter(''); setPriorityFilter(''); setSearch(''); setPage(1); }}
            style={{ padding: '7px 12px', border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff', cursor: 'pointer', fontSize: '0.875rem', color: '#64748b' }}>
            Clear
          </button>
        )}
      </div>

      {/* Task list */}
      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1rem' }}>
          {[1, 2, 3, 4, 5, 6].map(i => <SkeletonCard key={i} />)}
        </div>
      ) : data.data.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
          No tasks found.{' '}
          <button onClick={openAdd} style={{ background: 'none', border: 'none', color: '#2563eb', cursor: 'pointer', fontWeight: 600 }}>Create one</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.data.map(task => {
            const pm = priorityMeta[task.priority] || priorityMeta.medium;
            const sm = statusMeta[task.status] || statusMeta.todo;
            const overdue = task.status !== 'done' && task.status !== 'cancelled' && isOverdue(task.due_date);
            return (
              <div key={task.id} style={{
                background: '#fff', border: `1px solid ${overdue ? '#fca5a5' : '#e2e8f0'}`,
                borderLeft: `4px solid ${pm.border || '#e2e8f0'}`,
                borderRadius: 10, padding: '14px 18px',
                display: 'flex', alignItems: 'flex-start', gap: 14,
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
              }}>
                {/* Status checkbox */}
                <button
                  title={task.status === 'done' ? 'Mark as To Do' : 'Mark as Done'}
                  onClick={() => handleStatusToggle(task, task.status === 'done' ? 'todo' : 'done')}
                  style={{
                    width: 22, height: 22, borderRadius: 6, border: '2px solid #cbd5e1',
                    background: task.status === 'done' ? '#10b981' : '#fff',
                    cursor: 'pointer', flexShrink: 0, marginTop: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                  {task.status === 'done' && <span style={{ color: '#fff', fontSize: 13, fontWeight: 900 }}>✓</span>}
                </button>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{
                      fontWeight: 600, fontSize: '0.9rem', color: task.status === 'done' ? '#94a3b8' : '#1e293b',
                      textDecoration: task.status === 'done' ? 'line-through' : 'none',
                    }}>{task.title}</span>
                    <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 700,
                      background: pm.bg, color: pm.text, border: `1px solid ${pm.border}` }}>
                      {pm.label}
                    </span>
                    <span style={{ padding: '1px 8px', borderRadius: 10, fontSize: '0.7rem', fontWeight: 600,
                      background: sm.bg, color: sm.text }}>
                      {sm.label}
                    </span>
                  </div>
                  {task.description && (
                    <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: '0.8rem', lineHeight: 1.4 }}>
                      {task.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 14, marginTop: 6, flexWrap: 'wrap', fontSize: '0.75rem', color: '#94a3b8' }}>
                    {task.assigned_to_name && <span>👤 {task.assigned_to_name}</span>}
                    {task.due_date && (
                      <span style={{ color: overdue ? '#dc2626' : '#94a3b8', fontWeight: overdue ? 700 : 400 }}>
                        📅 {overdue ? 'Overdue · ' : ''}{fmtDate(task.due_date)}
                      </span>
                    )}
                    {task.entity_type && <span>🔗 {task.entity_type} #{task.entity_id}</span>}
                    <span>By {task.created_by_name}</span>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  {task.status === 'todo' && (
                    <button onClick={() => handleStatusToggle(task, 'in_progress')}
                      style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #93c5fd', background: '#dbeafe',
                        color: '#1d4ed8', fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600 }}>
                      Start
                    </button>
                  )}
                  <button onClick={() => openEdit(task)}
                    style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#f8fafc',
                      color: '#475569', fontSize: '0.72rem', cursor: 'pointer' }}>
                    Edit
                  </button>
                  <button onClick={() => setDeleteConfirm(task)}
                    style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fee2e2',
                      color: '#dc2626', fontSize: '0.72rem', cursor: 'pointer' }}>
                    Del
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {data.total > 15 && (
        <div style={{ marginTop: 20 }}>
          <Pagination page={page} total={data.total} limit={15} onChange={setPage} />
        </div>
      )}

      {/* Add / Edit Modal */}
      <Modal isOpen={modal.open} onClose={() => setModal({ open: false, mode: 'add', item: null })}
        title={modal.mode === 'add' ? 'New Task' : 'Edit Task'}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {error && <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#dc2626', borderRadius: 6, fontSize: '0.85rem' }}>{error}</div>}

          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Title *</label>
            <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              placeholder="What needs to be done?"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: '0.875rem', boxSizing: 'border-box' }} />
          </div>

          <div>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Description</label>
            <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3} placeholder="Optional details…"
              style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: '0.875rem', resize: 'vertical', boxSizing: 'border-box' }} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: '0.875rem' }}>
                {PRIORITIES.map(p => <option key={p} value={p}>{priorityMeta[p].label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Status</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: '0.875rem' }}>
                {STATUSES.map(s => <option key={s} value={s}>{statusMeta[s].label}</option>)}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Assign To</label>
              <select value={form.assigned_to} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: '0.875rem' }}>
                <option value="">— Unassigned —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Due Date</label>
              <input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: '0.875rem', boxSizing: 'border-box' }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Linked Entity Type</label>
              <select value={form.entity_type} onChange={e => setForm(f => ({ ...f, entity_type: e.target.value }))}
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: '0.875rem' }}>
                <option value="">— None —</option>
                {['inquiry','order','client','customer','project','service_request'].map(t => (
                  <option key={t} value={t}>{t.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Entity ID</label>
              <input type="number" value={form.entity_id} onChange={e => setForm(f => ({ ...f, entity_id: e.target.value }))}
                placeholder="e.g. 42"
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: '0.875rem', boxSizing: 'border-box' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 4 }}>
            <button onClick={() => setModal({ open: false, mode: 'add', item: null })}
              style={{ padding: '8px 18px', border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff', cursor: 'pointer', fontWeight: 600, color: '#64748b' }}>
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 600, cursor: 'pointer' }}>
              {saving ? 'Saving…' : modal.mode === 'add' ? 'Create Task' : 'Save Changes'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete confirm */}
      {deleteConfirm && (
        <Modal isOpen onClose={() => setDeleteConfirm(null)} title="Delete Task">
          <p style={{ color: '#374151', marginBottom: 20 }}>
            Delete <strong>"{deleteConfirm.title}"</strong>? This cannot be undone.
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setDeleteConfirm(null)}
              style={{ padding: '8px 18px', border: '1px solid #e2e8f0', borderRadius: 7, background: '#fff', cursor: 'pointer', color: '#64748b' }}>
              Cancel
            </button>
            <button onClick={() => handleDelete(deleteConfirm.id)}
              style={{ padding: '8px 18px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, fontWeight: 600, cursor: 'pointer' }}>
              Delete
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
